import { IconButton, Stack, Typography } from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { COLOR_FROM_CLASSIFICATION, ICON_FROM_CLASSIFICATION } from "library/SFG20/constants.mjs"
import Iconify from "minimals-template/components/Iconify"
import noop from "lib/noop"
import { isTailoringEvent } from "routes/regime/extension-edit/plugins/tailored-schedule-auditing/lib/is-tailoring-event"
import { AmendsChangeType } from "authoring/lib/enum/amends-change-type.mjs"
import { normaliseAuthoringAmendsAuditPayload } from "../lib/normalise-authoring-amends-audit-frequency"
import { AuthoringTailoredValue } from "./authoring-tailored-value"
import { UNIT_OF_MEASURE_AMENDS_KEY } from "../lib/constants/amends-keys"

export function AuthoringReviewAudit({
    payload,
    chipSx,
    titleVariant = "body2",
    titleSx,
    showViewAction = false,
    onView = noop,
}) {
    if (!isTailoringEvent(payload)) return null

    const normalisedPayload = normaliseAuthoringAmendsAuditPayload(payload) ?? {}
    const { taskTitle, changeType } = normalisedPayload
    const amendsKey = normalisedPayload.amendsKey ?? normalisedPayload.field

    const title = normaliseTaskTitle(taskTitle)
    const originalValues = sanitiseAuditValues(normalisedPayload.original)
    const tailoredValues = sanitiseAuditValues(normalisedPayload.tailored)
    const displayOriginal = { ...originalValues, changeType }
    const displayTailored = { ...tailoredValues, changeType }
    const tailoredEntries = buildTailoredEntries({ tailoredValues, changeType, amendsKey })
    if (!tailoredEntries.length) return null

    const scheduleTitle = normalisedPayload.scheduleTitle ?? ""
    const scheduleCode = normalisedPayload.scheduleCode ?? ""
    const currentClassification = displayTailored.classification ?? displayOriginal.classification

    return (
        <Stack sx={{ mb: 2, mt: 1 }} data-cy="review-audit">
            <If truthy={scheduleTitle && scheduleCode}>
                <Typography variant="subtitle2">
                    {scheduleTitle} <small>#{scheduleCode}</small>
                </Typography>
            </If>
            <ListItemBox spacing={1} sx={{ my: 0 }}>
                <If truthy={!!currentClassification}>
                    <Iconify
                        icon={ICON_FROM_CLASSIFICATION[currentClassification]}
                        sx={{ color: COLOR_FROM_CLASSIFICATION[currentClassification] }}
                    />
                </If>
                <Typography variant={titleVariant} sx={{ pr: 1, flex: 1, ...titleSx }}>
                    {title}
                </Typography>
                <If truthy={showViewAction}>
                    <IconButton
                        aria-label="View details"
                        color="primary"
                        size="small"
                        onClick={onView}
                        data-cy="authoring-amends-view-details"
                    >
                        <Iconify icon="ic:outline-remove-red-eye" />
                    </IconButton>
                </If>
            </ListItemBox>
            <Repeat
                list={tailoredEntries}
                item={<AuthoringTailoredValue original={displayOriginal} tailored={displayTailored} chipSx={chipSx} />}
            />
        </Stack>
    )
}

function normaliseTaskTitle(taskTitle) {
    if (taskTitle === "details") return "Title"
    if (taskTitle === "intro") return "Introduction"
    return taskTitle
}

function sanitiseAuditValues(values = {}) {
    const safeValues = values && typeof values === "object" ? values : {}
    const output = {}
    for (const [key, value] of Object.entries(safeValues)) {
        if (value == null) continue
        output[key] = value
    }

    if (!output.skill && !output.skillSet) {
        output.skillSet = "1"
        output.skill = {
            CoreSkillingID: "1",
            Skilling: "Unknown",
            SkillingCode: "??",
        }
    }

    return output
}

function buildTailoredEntries({ tailoredValues = {}, changeType, amendsKey }) {
    const lifecycleEntry = getLifecycleEntry(changeType)
    const entries = Object.entries(tailoredValues)
        .filter(
            ([key]) =>
                key !== "changeType" &&
                !key.startsWith("_") &&
                (key !== UNIT_OF_MEASURE_AMENDS_KEY || amendsKey === UNIT_OF_MEASURE_AMENDS_KEY)
        )
        .map(([key]) => key)

    if (!lifecycleEntry) return entries
    return [lifecycleEntry, ...entries]
}

function getLifecycleEntry(changeType) {
    if (changeType === AmendsChangeType.ADDED || changeType === AmendsChangeType.REMOVED) return "changeType"
    return null
}
